import { useEffect } from "react";
import {
  Backdrop,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { string, object, boolean } from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

import { LoadingBox } from "../../../../../../components/utils";
import {
  getSignatory,
  storeSignatory,
  updateSignatory,
} from "../../../../../services/pwsApi";

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "primary",
    backgroundColor: "rgba(0, 0, 0, 0.25) !important",
  },
}));

const signatorySchema = object().shape({
  nome: string().required("Informe o nome"),
  email: string()
    .required("Informe o e-mail")
    .email("Informe um e-mail válido"),
  ativo: boolean(),
});

export default function SignatoryDialog({ onClose, signatoryId }) {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const companyCode = searchParams.get("company_code");
  const company = companyCode?.slice(0, 3);
  const subsidiary = companyCode?.slice(3, 7);

  const signatoryQuery = useQuery(
    ["signatory", signatoryId],
    () => getSignatory(company, subsidiary, signatoryId),
    {
      enabled: !!signatoryId && !!company && !!subsidiary,
      select: response => {
        const signatory = response.data?.data;
        if (!signatory) return null;

        return {
          id: signatory.ASI_ID,
          nome: signatory.ASI_NOME,
          email: signatory.ASI_EMAIL,
          ativo: signatory.ASI_ATIVO === "1",
        };
      },
    },
  );

  const data = signatoryQuery.data;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(signatorySchema),
    defaultValues: {
      id: data?.id ?? null,
      nome: data?.nome ?? "",
      email: data?.email ?? "",
      ativo: data?.ativo ?? true,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        id: data.id ?? null,
        nome: data.nome ?? "",
        email: data.email ?? "",
        ativo: data.ativo ?? true,
      });
    }
  }, [data, reset]);

  const storeMutation = useMutation(storeSignatory, {
    onSuccess: () => {
      toast.success("Signatário criado com sucesso.");
      queryClient.invalidateQueries("signatories");
      queryClient.invalidateQueries("signatory");
      onClose();
    },
    onError: () => {
      toast.error("Erro ao criar signatário.");
    },
  });

  const updateMutation = useMutation(
    variables => updateSignatory(variables.id, variables),
    {
      onSuccess: () => {
        toast.success("Signatário atualizado com sucesso.");
        queryClient.invalidateQueries("signatories");
        queryClient.invalidateQueries("signatory");
        onClose();
      },
      onError: () => {
        toast.error("Erro ao atualizar signatário.");
      },
    },
  );

  const onSubmit = formData => {
    const { id, nome, email, ativo } = formData;

    const payload = {
      companyCode: company,
      subsidiaryCode: subsidiary,
      nome,
      email,
      ativo,
    };

    if (id) {
      updateMutation.mutate({ id, ...payload });
    } else {
      storeMutation.mutate(payload);
    }
  };

  return (
    <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
      <Backdrop
        className={classes.backdrop}
        open={signatoryQuery?.isLoading || storeMutation.isLoading || updateMutation.isLoading}
      >
        <LoadingBox />
      </Backdrop>
      <DialogTitle>
        {!data?.id ? "Adicionar signatário" : "Editar signatário"}
      </DialogTitle>

      <DialogContent dividers>
        <div className="row">
          <div className="col-md-12">
            <Controller
              name="nome"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="Nome"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.nome}
                  helperText={errors.nome?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="E-mail"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-12" style={{ marginTop: "16px" }}>
            <Controller
              name="ativo"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  }
                  label="Ativo"
                />
              )}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
        >
          {!data?.id ? "Adicionar" : "Editar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
